package com.isaias.fyattravel.shared.data.model

/**
 * A short Uganda safari package. Single source of truth for the website's
 * Explore/Categories pages and the Android/iOS app's Tours category, so both
 * platforms show the same packages without duplicating the data.
 */
data class SafariPackage(
    val park: String,
    val name: String,
    val duration: String,
    val highlights: List<String>,
    val imagePath: String,
    val imageAlt: String,
    val waMessage: String,
    val signature: Boolean = false
)

private const val PACKAGE_IMAGE_BASE = "https://fyattraveltour.com/images/explore/packages"

val SAFARI_PACKAGES: List<SafariPackage> = listOf(
    SafariPackage(
        park = "Bwindi Impenetrable National Park",
        name = "Gorilla Trekking",
        duration = "4 Days / 3 Nights",
        highlights = listOf(
            "Guided gorilla trekking permit & briefing",
            "Habituated mountain gorilla family encounter",
            "Local Batwa community visit",
            "Professional ranger-guide throughout"
        ),
        imagePath = "$PACKAGE_IMAGE_BASE/bwindi-gorilla-trekking.jpg",
        imageAlt = "Mountain gorilla in Bwindi Impenetrable Forest",
        waMessage = "Hi! I'd like to enquire about the Bwindi Gorilla Trekking package (4 Days / 3 Nights)."
    ),
    SafariPackage(
        park = "Queen Elizabeth National Park",
        name = "Big Cats & Kazinga Channel",
        duration = "4 Days / 3 Nights",
        highlights = listOf(
            "Game drives for lion, leopard & elephant",
            "Kazinga Channel boat cruise",
            "Tree-climbing lions of Ishasha",
            "Crater-lake scenic drive"
        ),
        imagePath = "$PACKAGE_IMAGE_BASE/queen-elizabeth-big-cats.jpg",
        imageAlt = "Lions walking across the savanna in Queen Elizabeth National Park",
        waMessage = "Hi! I'd like to enquire about the Queen Elizabeth Big Cats & Kazinga Channel package (4 Days / 3 Nights)."
    ),
    SafariPackage(
        park = "Murchison Falls National Park",
        name = "The Nile & The Falls",
        duration = "3 Days / 2 Nights",
        highlights = listOf(
            "Boat cruise to the base of the Falls",
            "Top-of-the-Falls viewpoint hike",
            "Big-game morning game drive",
            "Nile-side lodging"
        ),
        imagePath = "$PACKAGE_IMAGE_BASE/murchison-falls-nile.jpg",
        imageAlt = "The Nile crashing through the gorge at Murchison Falls, Uganda",
        waMessage = "Hi! I'd like to enquire about the Murchison Falls (The Nile & The Falls) package (3 Days / 2 Nights)."
    ),
    SafariPackage(
        park = "Kibale Forest National Park",
        name = "Chimpanzee Trekking",
        duration = "2 Days / 1 Night",
        highlights = listOf(
            "Guided chimpanzee trekking permit",
            "Primate-rich rainforest walk",
            "Bigodi Wetland nature walk option"
        ),
        imagePath = "$PACKAGE_IMAGE_BASE/kibale-chimpanzee-trekking.jpg",
        imageAlt = "Chimpanzee in the rainforest canopy of Kibale Forest",
        waMessage = "Hi! I'd like to enquire about the Kibale Forest Chimpanzee Trekking package (2 Days / 1 Night)."
    ),
    SafariPackage(
        park = "Lake Mburo National Park",
        name = "Weekend Safari",
        duration = "2 Days / 1 Night",
        highlights = listOf(
            "Closest savanna park to Kampala",
            "Zebra, impala & buffalo game drive",
            "Optional guided walking safari"
        ),
        imagePath = "$PACKAGE_IMAGE_BASE/lake-mburo-weekend.jpg",
        imageAlt = "Zebras grazing at Lake Mburo National Park, Uganda",
        waMessage = "Hi! I'd like to enquire about the Lake Mburo Weekend Safari package (2 Days / 1 Night)."
    ),
    SafariPackage(
        park = "Kidepo Valley National Park",
        name = "Wild Karamoja Safari",
        duration = "3 Days / 2 Nights",
        highlights = listOf(
            "Narus Valley game drive (lion, buffalo, elephant)",
            "Apoka Rim scenic viewpoint",
            "Karamojong homestead cultural visit",
            "Uganda's most remote & untouched park"
        ),
        imagePath = "$PACKAGE_IMAGE_BASE/kidepo-valley-karamoja.jpg",
        imageAlt = "Savanna landscape with the Napore Mountains at Kidepo Valley National Park",
        waMessage = "Hi! I'd like to enquire about the Kidepo Valley Wild Karamoja Safari package (3 Days / 2 Nights)."
    ),
    SafariPackage(
        park = "Jinja",
        name = "Busowoko Falls & Jinja City Tour",
        duration = "3 Days / 2 Nights",
        highlights = listOf(
            "Busowoko Falls visit",
            "Source of the Nile boat cruise at Jinja",
            "Guided Jinja city & town tour",
            "Optional Nile white-water rafting"
        ),
        imagePath = "$PACKAGE_IMAGE_BASE/jinja-source-of-nile.jpg",
        imageAlt = "Rafting team paddling through white-water rapids on the Nile near Jinja, Uganda",
        waMessage = "Hi! I'd like to enquire about the Busowoko Falls & Jinja City Tour package (3 Days / 2 Nights)."
    ),
    SafariPackage(
        park = "Bwindi + Queen Elizabeth + Murchison Falls + Kibale",
        name = "The Complete Pearl",
        duration = "10 Days / 9 Nights",
        highlights = listOf(
            "Gorilla trekking in Bwindi",
            "Big cats & Kazinga Channel cruise",
            "Chimpanzee trekking in Kibale",
            "The Nile & Murchison Falls finale"
        ),
        imagePath = "$PACKAGE_IMAGE_BASE/complete-pearl-combo.jpg",
        imageAlt = "Collage of Uganda's national parks — gorillas, big cats and the Nile",
        waMessage = "Hi! I'd like to enquire about The Complete Pearl signature package (10 Days / 9 Nights).",
        signature = true
    )
)
